import React, { useState } from 'react';
import { X, Download, Apple, Play } from 'lucide-react';
import { DeviceType } from '../../utils/deviceDetection';
import env from '../../config/env';

interface MobileAppDownloadPromptProps {
  deviceType: DeviceType;
  onDismiss: () => void;
}

export const MobileAppDownloadPrompt: React.FC<MobileAppDownloadPromptProps> = ({ 
  deviceType, 
  onDismiss 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleDismiss = () => {
    setIsAnimating(true);
    setTimeout(() => {
      onDismiss();
    }, 300);
  };

  const handleDownload = () => {
    const link = deviceType === 'ios' ? env.appStore.ios : env.appStore.android;
    window.open(link, '_blank');
  };

  const isIOS = deviceType === 'ios';
  const appStoreName = isIOS ? 'App Store' : 'Play Store';
  const StoreIcon = isIOS ? Apple : Play;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isAnimating ? 'opacity-0' : 'opacity-50'
        }`}
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 flex justify-center ${
          isAnimating ? 'translate-y-full' : 'translate-y-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="bg-background rounded-t-3xl w-full max-w-md p-6 space-y-4"
        >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">
            Get the Relay Mobile App
          </h3>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Benefits */}
        <div className="space-y-3">
          <div className="text-gray-600 text-sm">
            Download the mobile app for an optimized experience:
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Faster performance and smoother navigation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Push notifications for new calls and updates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Offline functionality for critical features</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Access to native device features (camera, location)</span>
            </li>
          </ul>
        </div>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          className="w-full bg-primary text-primary-foreground py-4 px-6 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
        >
          <Download size={20} />
          <span>Download from {appStoreName}</span>
        </button>

        {/* Dismiss Button */}
        <button
          onClick={handleDismiss}
          className="w-full py-3 px-6 border-2 border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          Continue in Browser
        </button>

        {/* Footer */}
        <div className="text-xs text-gray-500 text-center">
          You can always download the app later from the {appStoreName}
        </div>
        </div>
      </div>
    </>
  );
};

